/* facets.js — collection filters, sorting, AJAX pagination, and state sync */

class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 500);

    this.debouncedOnSubmitReduced = debounce((event) => {
      this.onSubmitHandler(event);
    }, 250, true);

    const wrap = this.querySelector('.facets-wrap');

    if (wrap) {
      // Keep input for price range (live feedback while typing)
      wrap.addEventListener('input', (event) => {
        if (event.target.closest('price-range')) {
          this.debouncedOnSubmit(event);
        }
      });

      // NEW: change handler for checkboxes/radios/selects to reliably trigger filtering
      wrap.addEventListener('change', (event) => {
        // price-range already handled above; everything else funnels here
        this.debouncedOnSubmitReduced(event);
      });

      // Optional: allow Enter on text inputs to apply immediately
      wrap.addEventListener('keyup', (event) => {
        if (event.key === 'Enter') this.debouncedOnSubmit(event);
      });
    }
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true, appendResults) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const collectionEl = document.getElementById('ProductGridContainer')?.querySelector('.collection');
    if (collectionEl) collectionEl.classList.add('loading');
    if (countContainer) countContainer.classList.add('loading');

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event, appendResults);
    });

    if (updateURLHash) {
      FacetFiltersForm.updateURLHash(searchParams);
    }

    if (!searchParams.includes('page')) {
      const filtersEvent = new CustomEvent('collection:product-filters', {
        detail: {
          filters: deserializeSearchParams(searchParams),
        },
      });
      document.dispatchEvent(filtersEvent);
    }
  }

  static renderSectionFromFetch(url, event, appendResults) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderCollectionActions(html);
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html, appendResults);
        FacetFiltersForm.renderProductCount(html);
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderCollectionActions(html);
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
  }

  static renderProductGridContainer(html, appendResults) {
    const container = document.getElementById('ProductGridContainer');
    const parsedDom = new DOMParser().parseFromString(html, 'text/html');

    if (appendResults) {
      parsedDom.getElementById('product-grid')
        .prepend(...[...document.getElementById('product-grid').childNodes]);
    }

    container.innerHTML = parsedDom.getElementById('ProductGridContainer').innerHTML;
  }

  static renderProductCount(html) {
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const count = parsed.getElementById('ProductCount')?.innerHTML;
    const container = document.getElementById('ProductCount');
    if (!container) return;
    container.innerHTML = count;
    container.classList.remove('loading');
  }

  static renderCollectionActions(html) {
    const container = document.getElementById('CollectionActions');
    if (!container) return;
    const parsedDom = new DOMParser().parseFromString(html, 'text/html');
    const parsedContainerActions = parsedDom.getElementById('CollectionActions');

    if (container.classList.contains('is-stuck')) {
      parsedContainerActions.classList.add('is-stuck');
    }

    container.classList = parsedContainerActions.classList;
    container.innerHTML = parsedContainerActions.innerHTML;
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');

    const facetDetailsElements = parsedHTML.querySelectorAll('#FacetFiltersForm .js-filter');
    const isPriceFilter = (element) => !!element.querySelector('price-range');

    const matchesIndex = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.dataset.index === jsFilter.dataset.index : false;
    };

    const facetsToRender = Array.from(facetDetailsElements).filter((element) => {
      if (isPriceFilter(element)) {
        return !matchesIndex(element);
      }
      return true;
    });

    facetsToRender.forEach((element) => {
      const oldElement = document.querySelector(`.js-filter[data-index="${element.dataset.index}"]`);
      // Preserve expanded status of the old element
      element.querySelector('.facet-toggle')
        .setAttribute('aria-expanded', oldElement.querySelector('.facet-toggle').getAttribute('aria-expanded'));
      element.querySelector('collapsible-expandable')
        .setAttribute('expanded', oldElement.querySelector('collapsible-expandable').getAttribute('expanded'));
      oldElement.innerHTML = element.innerHTML;
    });

    // Special handling for the price range to preserve focus
    const priceFilter = Array.from(facetDetailsElements).find(isPriceFilter);
    if (priceFilter) {
      document.querySelector('.js-price-range-header').innerHTML =
        priceFilter.querySelector('.js-price-range-header').innerHTML;
    }

    FacetFiltersForm.renderActiveFacets(parsedHTML);
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.collection-actions-filters'];
    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    });
    FacetFiltersForm.toggleActiveFacets(false);
  }

  static updateURLHash(searchParams, replace) {
    const url = `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`;
    if (replace) {
      history.replaceState({ searchParams }, '', url);
    } else {
      history.pushState({ searchParams }, '', url);
    }
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const formData = new FormData(document.getElementById('FacetsFilterForm'));
    const searchParams = new URLSearchParams(formData).toString();
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url = event.currentTarget.href.indexOf('?') === -1
      ? ''
      : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }

  onAjaxPagination(event) {
    event.preventDefault();
    const next = event.currentTarget;
    const url = next.href.indexOf('?') === -1
      ? ''
      : next.href.slice(next.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url, event, true);
    document.getElementById('MainContent').scrollIntoView(true);

    const params = new URLSearchParams(url);
    const customEvent = new CustomEvent('pagination:page-change', {
      detail: { page: Number(params.get('page')) },
    });
    document.dispatchEvent(customEvent);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

/* ----- Price Range component ----- */
class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input')
      .forEach((element) => element.addEventListener('change', this.onRangeChange.bind(this)));
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('min', 0);
    if (maxInput.value === '') minInput.setAttribute('max', minInput.getAttribute('max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('min'));
    const max = Number(input.getAttribute('max'));
    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}
customElements.define('price-range', PriceRange);

/* ----- Active facet remove link ----- */
class FacetRemove extends HTMLElement {
  constructor() {
    super();
    this.querySelector('a').addEventListener('click', (event) => {
      event.preventDefault();
      const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
      form.onActiveFilterClick(event);
    });
  }
}
customElements.define('facet-remove', FacetRemove);

/* ----- AJAX pagination (load next page into grid) ----- */
class AjaxPaginate extends HTMLElement {
  constructor() {
    super();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    const buttons = this.querySelectorAll('a');
    buttons.forEach((button) => {
      button.addEventListener('click', (event) => {
        event.preventDefault();
        form.onAjaxPagination(event);
      });
    });
  }
}
customElements.define('ajax-paginate', AjaxPaginate);

/* ----- Sticky collection actions (filters bar) ----- */
class StickyCollectionActions extends HTMLElement {
  constructor() {
    super();
    this.sticky = this.children[0];
    this.createObserver();
  }

  createObserver() {
    const observer = new IntersectionObserver(
      ([entry]) => {
        entry.target.classList.toggle('is-stuck', entry.boundingClientRect.top < 0);
      },
      { threshold: 1 }
    );
    observer.observe(this.sticky);
  }
}
customElements.define('sticky-collection-actions', StickyCollectionActions);

/* ----- helper: deserialize querystring to object ----- */
const deserializeSearchParams = (queryString) => {
  const params = new URLSearchParams(queryString);
  const result = {};
  for (const [key, value] of params.entries()) {
    if (result[key]) {
      result[key] = `${result[key]},${value}`;
    } else if (value) {
      result[key] = value;
    }
  }
  return result;
};
